import { FilterItem } from "@sscs/types";
import { AUDIT_ENDPOINT } from "Constants";
import { baseApi, listenToUserUpdates } from "Services";
import { ExportJob, UserUpdatesMessage } from "Types";
import {
  AuditEnums,
  AuditFilters,
  AuditRequestFilterItem,
  ExportAuditLogsRequest,
  ExportJobsRequest,
  ExportJobsResponse,
  SearchAuditLogsRequest,
  SearchAuditLogsResponse,
} from "../types";
import { updateExportJob } from "./websocketUpdates";

export const addTagTypes = ["ExportJobs"] as const;

export const auditApi = baseApi
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      searchAuditLogs: build.query<
        SearchAuditLogsResponse,
        SearchAuditLogsRequest
      >({
        query: (queryArg) => ({
          url: `${AUDIT_ENDPOINT}/search`,
          method: "POST",
          body: {
            ...queryArg.body,
            filters: Object.entries(queryArg.body.filters).map(
              ([filterName, filter]: [
                keyof AuditFilters,
                FilterItem,
              ]): AuditRequestFilterItem => {
                return { filter_by: filterName, ...filter };
              },
            ),
          },
        }),
      }),
      exportAuditLogs: build.mutation<ExportJob, ExportAuditLogsRequest>({
        query: (queryArg) => ({
          url: `${AUDIT_ENDPOINT}/export-jobs/create`,
          method: "POST",
          body: {
            ...queryArg.body,
            filters: Object.entries(queryArg.body.filters).map(
              ([filterName, filter]: [
                keyof AuditFilters,
                FilterItem,
              ]): AuditRequestFilterItem => {
                return { filter_by: filterName, ...filter };
              },
            ),
          },
        }),
        invalidatesTags: ["ExportJobs"],
      }),
      getAuditExports: build.query<ExportJobsResponse, ExportJobsRequest>({
        query: (queryArg) => ({
          url: `${AUDIT_ENDPOINT}/export-jobs`,
          method: "GET",
          params: {
            limit: queryArg.limit,
            offset: queryArg.offset,
          },
        }),
        async onCacheEntryAdded(
          arg,
          { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
        ) {
          const listener = (event: MessageEvent) => {
            const message = JSON.parse(event.data) as UserUpdatesMessage;

            if (
              message.entity === "export_job" &&
              message.action === "updated"
            ) {
              updateCachedData((draft) => {
                return updateExportJob(draft, message);
              });
            }
          };

          await listenToUserUpdates(
            cacheDataLoaded,
            cacheEntryRemoved,
            listener,
          );
        },
        providesTags: ["ExportJobs"],
      }),
      deleteAuditExport: build.mutation<void, string>({
        query(exportJobId) {
          return {
            url: `${AUDIT_ENDPOINT}/export-jobs/${exportJobId}`,
            method: "DELETE",
          };
        },
        invalidatesTags: ["ExportJobs"],
      }),
      getAuditEnums: build.query<AuditEnums, void>({
        query: () => ({
          url: `${AUDIT_ENDPOINT}/enums`,
          method: "GET",
        }),
      }),
    }),
  });

export const {
  useSearchAuditLogsQuery,
  useExportAuditLogsMutation,
  useGetAuditExportsQuery,
  useDeleteAuditExportMutation,
  useLazyGetAuditEnumsQuery,
} = auditApi;
